import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import useAxios from "../../hooks/useAxios";
import useCommonActions from "../../hooks/useCommonActions";
import { ContentLoader, PageToolbar, Pagination, TooltipWrapper, RatingStar } from "../common";
import badges from "../../_helpers/badgeHelper";
import helpers from "../../_helpers/common";
import { hasAuthorization } from "../../_helpers/hasAuthorization";
import SearchForm from "./partial/SearchForm";
import CommonList from "../common/CommonList";
import { KundliPredictionSearchFormState, KundliPrediction, Category } from "../../types/interfaces";

const List: React.FC = () => {
  const axiosInstance = useAxios();
  const { deleteItem } = useCommonActions();
  const [searchForm, setSearchForm] = useState<KundliPredictionSearchFormState>({ search_string: "", category: "", status: "" });
  const [results, setResults] = useState<KundliPrediction[] | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10 });
  const [isLoading, setIsLoading] = useState(false);
  const isInitialRender = useRef(false);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    getListData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);
  
  /*Handle pagination data change*/
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ currentPage: page, pageSize });
  };

  const getListData = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const response: any = await axiosInstance({
        url: "kundli-prediction/admin-product-list",
        method: "GET",
        data: {
          page: pagination.currentPage,
          limit: pagination.pageSize,
          search_string: searchForm.search_string,
		  category: searchForm.category,
          status: searchForm.status,
        },
      });
      // console.log("event list response", response);
	  if(!response.error){
        setResults(response.data.products.docs);
        setTotalItems(response.data.products.totalDocs);
	  }
    } catch (error: any) {
      console.error("Error in api request:", error);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[pagination,searchForm]);
  
  /*Delete selected item*/
  const deleteProduct = async (itemid?: string) => {
    const message = `Are you sure you want to delete the Product?`;
    deleteItem(`kundli-prediction/delete-product/${itemid}`, message, getListData);
  };

  return (
   <>
      <div className="d-flex justify-content-between">
        {/*Page toolbar Area Start*/}
        <PageToolbar title="Kundli Prediction List" />
		{hasAuthorization("add", "kundli_prediction") && (
          <Link
			className="btn btn-flex btn-primary h-40px fs-7 fw-bold"
			to={`/create-update-kundli-prediction/0`}
		  >
			Create New
		  </Link>
		)}
      </div>
	  <div id="kt_content" className="content flex-column-fluid">
        {/*Search Area Start*/}
        <SearchForm categories={categories} searchForm={searchForm} setSearchForm={setSearchForm} handleSearchClick={getListData}/>
        {/*Search Area End*/}
        {/*Content Area Start*/}
        <div className={`card ${isLoading ? "overlay overlay-block" : ""}`}  style={{ minHeight:"70vh", maxHeight: "70vh" }} >
          <div className="card-body pt-0 pb-0 table-responsive">
		    <table className="table table-hover table-row-bordered table-row-solid gy-4 align-middle fw-bold w-100">
			  <thead
				style={{
				  position: "sticky",
				  top: 0,
				  zIndex: 5,
				  backgroundColor: `${isLoading ? "#BBBCBE" : "white"}`,
				  boxShadow: "0 4px 2px -2px rgba(190, 190, 190, 0.2)",
				}}
			  >
				<tr className="text-start text-gray-800 fw-bold fs-7 text-uppercase gs-0">
				  <th className="min-w-250px">Name</th>
				  <th className="min-w-100px">Category</th>
				  <th className="min-w-100px">Sales Price</th>
				  <th className="min-w-150px">Upfront Amount</th>
				  <th className="min-w-70px">Status</th>
				  <th className="min-w-150px">Created Date</th>
				  <th className="min-w-70px">Actions</th>
				</tr>
			  </thead>
			  <tbody className="fw-semibold text-gray-600">
				{!results || results.length === 0 ? (
				  <tr className="text-center">
					<td colSpan={10}>No Content Found</td>
				  </tr>
				) : (
				  results.map((item) => (
					<tr key={item._id}>
					  <td>
						<div className="d-flex align-items-center">
						  <div className="symbol symbol-square symbol-55px overflow-hidden me-3">
							<div className="symbol-label">
							  <img
								src={item?.displaypath}
								alt={item?.title}
								className="w-100"
							  />
							</div>
						  </div>
						  <div className="d-flex flex-column justify-content-center">
							<TooltipWrapper title={helpers.capitalizeFirstLetterOfEachWord(item?.title)} placement="bottom">
							  <span
								className="fw-semibold fs-6 text-gray-500 d-inline-block"
							  >
								{helpers.capitalizeFirstLetterOfEachWord(item?.title) || "N/A"}
							  </span>
							</TooltipWrapper>
						  </div>
						</div>
					  </td>
					  <td>
						  {typeof item?.category_id === "object"
							? item.category_id?.title
							: "N/A"}
					  </td>
					  <td className="">{`Rs. ${item.sales_price}` || "0"}</td>
					  <td className="">{`Rs. ${item.upfront_amount}` || "0"}</td>
					  <td>
						{badges.eventStatusBadge(
						  Number(item.status) ?? 0,
						  item?.statusname ?? ""
						)}
					  </td>
					  <td>
						{helpers.convertDateTime(
						  item?.created_at,
						  "DD MMM YYYY"
						) || "N/A"}
					  </td>
					  <td className="text-end">
						<div className="d-flex justify-content-end">
						   {hasAuthorization("edit", "kundli_prediction") && (
							<TooltipWrapper title="Edit" placement="top">
							  <Link
								className="btn btn-sm btn-icon btn-light btn-active-light-primary me-2"
								to={`/create-update-kundli-prediction/${item._id}`}
							  >
								<i className="ki-outline ki-notepad-edit text-warning fs-2"></i>
							  </Link>
							</TooltipWrapper>
						  )}
						  {hasAuthorization("view", "kundli_prediction") && (
							<TooltipWrapper title="View Details" placement="top">
							  <Link
								className="btn btn-sm btn-icon btn-light btn-active-light-primary me-2"
								to={`/kundli-prediction-detail/${item._id}`}
							  >
								<i className="ki-outline ki-eye text-primary fs-2"></i>
							  </Link>
							</TooltipWrapper>
						  )}
						  {hasAuthorization("delete_product", "kundli_prediction") && (
							<TooltipWrapper title="Delete Product" placement="top">
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-danger"
								onClick={() => deleteProduct(item?._id)}
							  >
								<i className="ki-outline ki-trash text-danger fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}
						</div>
					  </td>
					</tr>
				  ))
				)}
			  </tbody>
			</table>
		  </div>
          {/*Content Area End*/}
          {/*Pagination Start*/}
          <div
            className={`sticky-bottom bg-white border rounded p-2 shadow-sm ${
              isLoading ? "" : "border-top"
            }`}
            style={{
              bottom: 0,
              zIndex: 2,
              // backgroundColor: `${isLoading ? "#BBBCBE" : "white"}`,
            }}
          >
            {results && results.length > 0 ? (
              <Pagination
                totalItems={totalItems}
                itemsPerPage={pagination.pageSize}
                onPageChange={handlePageChange}
              />
            ) : null}
          </div>
          {/*Pagination End*/}
          {/*Content Loader Start*/}
          {isLoading ? <ContentLoader /> : null}
          {/*Content Loader End*/}
        </div>
      </div>
   </>
  );
};

export default List;